/**
 * 配送日時ピッカー。
 *
 * 選べる日付と時間帯は carrier_service の delivery_options が決める。ここには
 * 所要日数も休業日も持たない。以前は火・土だけを休みとして持っていて、農園の
 * 臨時休業 (DynamoDB の NoenDayOff) を知らないまま最短日と最長日の窓しか見て
 * いなかったので、窓の内側にある休業日をまたぐ日付が選べてしまった。
 * 8/13-15 が休みでも 8/14・8/15 着が通った (S26707, S26706)。
 *
 * 日付が変わると選べる時間帯も変わる。1日前倒しで出した荷物は前日のうちに
 * 配達店に着くので全時間帯選べる。前倒し先が休業日なら通常の出荷になり、
 * ヤマトの最早時間帯 (東京なら14時) より前は落ちる。地域によっては時間帯指定
 * そのものができない。
 *
 * 日付と時間帯はボタンで並べる。以前はプルダウンで、日付を変えると選べなくなった
 * 時間帯を黙って外していたため「選んだのに消えた」ように見えた。今は組み合わせられ
 * ないボタンも押せるようにして「〜が指定なしに戻ります」と書いておき、押したら
 * 相手を指定なしに戻して、その旨を案内する。ボタンは4種類:
 *   normal (通常) / caution (「▲ 鮮度面で非推奨」。前倒しで1日保管になる。選べる) /
 *   reset (押すと相手が指定なしに戻る) / disabled (指定不可)
 */
class DeliveryDateSelector {
  constructor() {
    this.apiUrl = 'https://ybzwe1cm43.execute-api.ap-northeast-1.amazonaws.com/prod/';
    this.slotLabels = {
      '0812': '午前中',
      1416: '14時〜16時',
      1618: '16時〜18時',
      1820: '18時〜20時',
      1921: '19時〜21時',
    };
    // ボタン用の短い表記
    this.slotShortLabels = {
      '0812': '午前中',
      1416: '14〜16時',
      1618: '16〜18時',
      1820: '18〜20時',
      1921: '19〜21時',
    };
    this.allSlots = Object.keys(this.slotLabels).sort(); // "0812" < "1416" < … で時刻順になる
    this.options = null; // 直近の delivery_options 応答
    this.fetchToken = 0; // 応答の追い越し対策
    this.dateResetMsg = ''; // 日付を指定なしに戻したときの案内
    this.timeResetMsg = ''; // 時間帯を指定なしに戻したときの案内
    this.init();
  }

  init() {
    this.checkbox = document.getElementById('delivery-date-checkbox');
    this.fields = document.getElementById('delivery-date-fields');
    this.dateInput = document.getElementById('delivery-date-input');
    this.timeInput = document.getElementById('delivery-time');
    this.dateChoices = document.getElementById('delivery-date-choices');
    this.timeChoices = document.getElementById('delivery-time-choices');
    this.zipInput = document.getElementById('shipping-zip');
    this.zipProvince = document.getElementById('shipping-zip-province');
    this.errorDisplay = document.getElementById('delivery-date-error');
    this.hintDisplay = document.getElementById('delivery-date-hint');
    this.dateNotice = document.getElementById('delivery-date-notice');
    this.timeHint = document.getElementById('delivery-time-hint');
    this.timeNotice = document.getElementById('delivery-time-notice');
    this.zipWarning = document.getElementById('prefecture-warning');

    if (!this.checkbox || !this.dateInput || !this.timeInput || !this.dateChoices || !this.timeChoices) return;

    // カート属性の値を使うのは、最初に選択肢が出たときの1回だけ (applyOptions で消す)。
    // 残しておくと、お客様が「指定なし」に戻したあと郵便番号を変えたときに古い値が戻る。
    this.savedDate = (this.dateInput.dataset.savedDate || '').replace(/\//g, '-');
    this.savedTime = this.timeInput.dataset.savedTime || '';
    this.maxDays = parseInt(this.dateInput.dataset.maxDays, 10) || 7;

    this.restoreZip();
    this.bindEvents();
    this.updateZipWarning();
    this.render();

    if (this.zipInput && this.zipInput.value) this.fetchOptions();
  }

  /**
   * 前回入力した郵便番号をこのブラウザから戻す。
   *
   * 郵便番号欄の初期値はログイン中のお客様の既定の住所だけなので、ゲストや別の宛先
   * (ギフトなど) を入れたお客様は、読み込み直すたびに入力し直しになっていた。
   * お届け日・時間帯はカート属性に残るので、郵便番号だけ空になって「郵便番号を
   * 入力してください」が出ていた。最後に入力したのが届け先なので、既定の住所より優先する。
   * カート属性にしないのは、注文データに余計な属性を混ぜないため。
   * localStorage はプライベートモードなどで使えないことがあるので、失敗しても何もしない。
   */
  restoreZip() {
    if (!this.zipInput) return;
    try {
      const saved = window.localStorage.getItem(DeliveryDateSelector.ZIP_KEY);
      if (saved && this.normalizeZip(saved)) this.zipInput.value = saved;
    } catch (e) {
      // 使えなければ既定の住所のまま
    }
  }

  /** 引けた郵便番号だけ覚える。空にされたら忘れる。途中まで打った番号では消さない */
  storeZip(value) {
    try {
      if (value) window.localStorage.setItem(DeliveryDateSelector.ZIP_KEY, value);
      else window.localStorage.removeItem(DeliveryDateSelector.ZIP_KEY);
    } catch (e) {
      // 覚えられなくても入力はできる
    }
  }

  bindEvents() {
    this.checkbox.addEventListener('change', () => {
      this.toggleFields();
      this.saveAttributes();
    });

    if (this.zipInput) {
      let debounce;
      const onZip = () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => this.fetchOptions(), 300);
      };
      this.zipInput.addEventListener('input', onZip);
      this.zipInput.addEventListener('change', onZip);
    }

    this.dateChoices.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-value]');
      if (btn && !btn.disabled) this.pickDate(btn.dataset.value);
    });
    this.timeChoices.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-value]');
      if (btn && !btn.disabled) this.pickTime(btn.dataset.value);
    });

    this.hookFormSubmit();
  }

  hookFormSubmit() {
    document.querySelectorAll('form[action="/cart"], form[action*="/checkout"]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        if (!this.validate()) {
          e.preventDefault();
          e.stopPropagation();
          this.errorDisplay.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (btn && !this.validate()) {
        e.preventDefault();
        e.stopPropagation();
        this.errorDisplay.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  normalizeZip(raw) {
    const s = String(raw || '')
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/\D/g, '');
    return s.length === 7 ? s : null;
  }

  fetchOptions() {
    const zip = this.normalizeZip(this.zipInput && this.zipInput.value);
    if (!zip) {
      // 引いている途中の応答を捨てる。捨てないと、番号を消したあとに古い番号の応答が
      // 届いて、その番号のお届け日が出てしまう (途中まで打った番号を覚えてもしまう)
      ++this.fetchToken;
      if (this.zipInput && !this.zipInput.value.trim()) this.storeZip('');
      this.options = null;
      this.applyOptions();
      this.updateZipWarning();
      if (this.zipProvince) this.zipProvince.textContent = '';
      return;
    }

    // API Gateway に設定されているのは POST だけ。GET は 403 になる。
    const token = ++this.fetchToken;
    fetch(this.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ delivery_options: { zip: zip } }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (token !== this.fetchToken) return; // 古い応答は捨てる
        if (!data || !data.found) throw new Error(data && data.reason);
        this.options = data;
        this.storeZip(`${zip.slice(0, 3)}-${zip.slice(3)}`); // 戻したときに読みやすい形で
        if (this.zipProvince) this.zipProvince.textContent = data.province || '';
        this.applyOptions();
        this.updateZipWarning();
        // 送料計算は県コードが要る。郵便番号から引けたものを渡す。
        window.dispatchEvent(new CustomEvent('delivery-options-loaded', { detail: data }));
      })
      .catch(() => {
        if (token !== this.fetchToken) return;
        this.options = null;
        if (this.zipProvince) this.zipProvince.textContent = '';
        this.applyOptions();
        this.updateZipWarning();
        this.showError('郵便番号からお届け日を取得できませんでした。番号をご確認ください。');
      });
  }

  /**
   * 出荷日 (DB_shipping_date) の 17:11 (JST) が今から7日 (168時間) 以内に収まる日だけを出す。
   * クレジットカードのオーソリは7日で切れるので、出荷 (=売上確定) がそれより後に
   * なる日付は受けない。以前は「最短お届け日 + 区間設定の最大予約日数」だけで
   * 切っていたので、最短日が注文日から離れるほど上限も後ろへずれ、S28093
   * (10/11 注文・10/19 出荷) のように8日を超えた。最大予約日数は上限として残す。
   * API は最短日から14日ぶん返す。
   */
  visibleDates() {
    if (!this.options || !this.options.dates.length) return [];
    const limitIso = this.addDaysIso(this.options.dates[0].date, this.maxDays);
    const deadline = this.authDeadlineMs();
    // ISO の日付は辞書順が日付順なので、そのまま文字列で比べられる
    const dates = this.options.dates.filter(
      (entry) => entry.date <= limitIso && this.shipCutoffMs(entry) <= deadline,
    );
    // 長い休業で最短の出荷が7日より先になると1日も残らない。そのときは最短日だけ出す。
    // 指定なしでも同じ日に出荷するので、指定させてもオーソリの期限は変わらない。
    return dates.length ? dates : [this.options.dates[0]];
  }

  /** 長い休業で、7日を超えた最短日だけを出しているとき */
  onlyEarliestBeyondLimit() {
    const dates = this.visibleDates();
    return dates.length === 1 && this.shipCutoffMs(dates[0]) > this.authDeadlineMs();
  }

  /** オーソリの期限。今から7日 (168時間) 後のエポックミリ秒 */
  authDeadlineMs() {
    return Date.now() + 7 * DeliveryDateSelector.DAY_MS;
  }

  /**
   * 注文取込 (netshopNewOrderAddDB の get_shipping_date) が DB_shipping_date に入れる日付。
   *
   * 取込側は「お届け日 - 所要日数」をそのまま DB_shipping_date にし、休業日で前倒しした
   * 実際の作業日は DB_prep_date に別に入れる。B2 の出荷予定日もオーソリ期限の警告も
   * DB_shipping_date で動くので、7日の判定もこちらで見る。API の ship_date は前倒し後の
   * 日付なので、休業日をはさむ日は1日早く出てしまう。午前中指定などで取込側の所要日数が
   * 1日延びる (= DB_shipping_date が早まる) ぶんは、時間帯を選ぶ前なので見込まない。
   */
  dbShippingDate(entry) {
    const base = this.addDaysIso(entry.date, -(this.options.transit_days || 0));
    return base > entry.ship_date ? base : entry.ship_date;
  }

  /** その日の DB_shipping_date の 17:11 JST (= 08:11 UTC) のエポックミリ秒 */
  shipCutoffMs(entry) {
    return this.isoToUtcMs(this.dbShippingDate(entry)) + (17 - 9) * 60 * 60 * 1000 + 11 * 60 * 1000;
  }

  /** "2026-10-19" -> その日の 00:00 UTC のエポックミリ秒。端末の時間帯に左右されない */
  isoToUtcMs(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  }

  /** ISO 日付を days 日ずらす */
  addDaysIso(iso, days) {
    return new Date(this.isoToUtcMs(iso) + days * DeliveryDateSelector.DAY_MS).toISOString().slice(0, 10);
  }

  /**
   * 新しい delivery_options (または null) を受けて、いまの選択がまだ使えるかを確かめる。
   * 使えなくなった値は黙って消さず、指定なしに戻したことを案内する。
   */
  applyOptions() {
    if (!this.options) {
      this.render();
      return;
    }

    const prevDate = this.dateInput.value || this.savedDate;
    const prevTime = this.timeInput.value || this.savedTime;
    this.savedDate = '';
    this.savedTime = '';
    this.dateResetMsg = '';
    this.timeResetMsg = '';

    const dateOk = !prevDate || this.visibleDates().some((d) => d.date === prevDate);
    const date = dateOk ? prevDate : '';
    if (!dateOk) {
      this.dateResetMsg = `${this.formatDateJP(prevDate)}は現在ご指定いただけないため、お届け日を指定なしに戻しました。`;
    }

    const timeOk = this.timeAllowed(date ? this.entryFor(date) : null, prevTime);
    // 時間帯を指定できない地域は timeHint が理由を言うので、ここでは重ねない
    if (!timeOk && this.options.time_slot_available) {
      const label = this.slotLabels[prevTime] || prevTime;
      this.timeResetMsg = date
        ? `${this.unavailableReason(this.entryFor(date), prevTime)}時間帯を指定なしに戻しました。`
        : `${label}は現在ご指定いただけないため、時間帯を指定なしに戻しました。`;
    }

    this.dateInput.value = date;
    this.timeInput.value = timeOk ? prevTime : '';
    this.render();
    // 選び直さずに購入へ進んでも古い値が注文に載らないように
    if ((!dateOk || !timeOk) && this.checkbox.checked) this.saveAttributes();
  }

  /** 日付ボタン。いまの時間帯がその日に届かないなら、時間帯を指定なしに戻す */
  pickDate(date) {
    this.dateResetMsg = '';
    this.timeResetMsg = '';
    const time = this.timeInput.value;
    if (!this.timeAllowed(date ? this.entryFor(date) : null, time)) {
      this.timeInput.value = '';
      this.timeResetMsg = `${this.unavailableReason(this.entryFor(date), time)}時間帯を指定なしに戻しました。`;
    }
    this.dateInput.value = date;
    this.afterPick();
  }

  /** 時間帯ボタン。いまの日付にその時間帯が届かないなら、日付を指定なしに戻す */
  pickTime(time) {
    this.dateResetMsg = '';
    this.timeResetMsg = '';
    const entry = this.selectedEntry();
    if (entry && !this.timeAllowed(entry, time)) {
      this.dateInput.value = '';
      this.dateResetMsg = `${this.unavailableReason(entry, time)}お届け日を指定なしに戻しました。`;
    }
    this.timeInput.value = time;
    this.afterPick();
  }

  /**
   * その日にその時間帯が届かない理由を、お客様向けの1文にする。
   *
   * 日付ごとに選べなくなる時間帯は、ヤマトの最早時間帯より前 (東京の午前中など) だけ。
   * 前日のうちに配達店へ着けるため1日前倒しで発送する必要があり、その発送日が
   * 農園の休業日 (月曜着なら土曜) か、最短の発送日より前 (準備が間に合わない) だと
   * 届けられない。前倒しは1日まで (carrier_service の MAX_HOLD_DAYS)。
   * 理由を書かないと「なぜ指定なしに戻ったのか」が分からない。
   */
  unavailableReason(entry, slot) {
    const label = this.slotLabels[slot] || slot;
    const late = (this.options && this.options.late_slots) || [];
    if (!entry || late.indexOf(slot) !== -1) return `${entry ? this.formatDateJP(entry.date) : ''}は${label}のお届けができません。`;

    const shipIso = this.addDaysIso(entry.date, -((this.options.transit_days || 0) + 1));
    const earliest = this.options.earliest_ship_date;
    // 時間帯の都合の前倒しは実際に1日早く発送するので「発送」と書く
    const why = earliest && shipIso < earliest ? 'ご注文からでは間に合わない' : '農園の休業日の';
    return (
      `${this.formatDateJP(entry.date)}${label}のお届けには${this.formatDateJP(shipIso)}の発送が必要ですが、` +
      `${why}ためお受けできません。`
    );
  }

  afterPick() {
    this.render();
    this.validate();
    this.saveAttributes();
  }

  entryFor(date) {
    return (this.options && this.options.dates.find((d) => d.date === date)) || null;
  }

  /**
   * その日付 (null は指定なし) と時間帯の組み合わせで注文できるか。
   * 指定なしなら、14日ぶんのどこかで選べる時間帯は取込側が届く日を決めてくれる。
   */
  timeAllowed(entry, time) {
    if (!time) return true;
    if (!this.options || !this.options.time_slot_available) return false;
    return (entry ? entry.slots : this.unspecifiedDateSlots()).indexOf(time) !== -1;
  }

  render() {
    this.renderDates();
    this.renderTimes();
    this.renderDateNotice();
    this.renderTimeNotice();
  }

  renderDates() {
    const dates = this.visibleDates();
    const box = this.dateChoices;
    box.innerHTML = '';

    if (!dates.length) {
      const p = document.createElement('p');
      p.className = 'delivery-choices__empty';
      p.textContent = this.options ? 'お届けできる日がありません' : '郵便番号を入力してください';
      box.appendChild(p);
      this.hintDisplay.textContent = '';
      return;
    }

    const current = this.dateInput.value;
    const time = this.timeInput.value;
    box.appendChild(this.choiceButton('', '指定なし', '最短でお届け', 'normal', current === ''));
    dates.forEach((d) => {
      let state = 'normal';
      let sub = '';
      if (!this.timeAllowed(d, time)) {
        state = 'reset';
        sub = '時間帯が指定なしに戻ります';
      } else if (this.holdDays(d) > 0 || this.slotHoldDays(d, time) > 0) {
        state = 'caution';
        sub = '▲ 鮮度面で非推奨';
      }
      box.appendChild(this.choiceButton(d.date, this.formatDateShort(d.date), sub, state, current === d.date));
    });

    this.hintDisplay.textContent = this.onlyEarliestBeyondLimit()
      ? `農園の休業のため、ご指定いただけるのは最短の${this.formatDateJP(dates[0].date)}のみです`
      : '';
  }

  renderTimes() {
    const box = this.timeChoices;
    box.innerHTML = '';
    this.timeHint.textContent = '';
    if (!this.options || !this.options.dates.length) return;

    if (!this.options.time_slot_available) {
      this.timeHint.textContent = 'この地域は時間帯のご指定に対応していません。';
      return;
    }

    const entry = this.selectedEntry();
    const anyDay = this.unspecifiedDateSlots();
    const current = this.timeInput.value;
    box.appendChild(this.choiceButton('', '指定なし', '', 'normal', current === ''));
    this.allSlots.forEach((s) => {
      let state = 'normal';
      let sub = '';
      if (anyDay.indexOf(s) === -1) {
        state = 'disabled';
        sub = '指定不可';
      } else if (!this.timeAllowed(entry, s)) {
        state = 'reset';
        sub = '日付が指定なしに戻ります';
      } else if (this.slotHoldDays(entry, s) > 0) {
        state = 'caution';
        sub = '▲ 鮮度面で非推奨';
      }
      box.appendChild(this.choiceButton(s, this.slotShortLabels[s] || s, sub, state, current === s));
    });
  }

  /** state: normal / caution (鮮度面で非推奨) / reset (押すと相手が指定なしに戻る) / disabled (指定不可) */
  choiceButton(value, main, sub, state, selected) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `delivery-choice delivery-choice--${state}${selected ? ' is-selected' : ''}`;
    btn.dataset.value = value;
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    if (state === 'disabled') btn.disabled = true;

    const m = document.createElement('span');
    m.className = 'delivery-choice__main';
    m.textContent = main;
    btn.appendChild(m);
    if (sub) {
      const s = document.createElement('span');
      s.className = 'delivery-choice__sub';
      s.textContent = sub;
      btn.appendChild(s);
    }
    return btn;
  }

  /**
   * parts は文字列か {strong: 文字列} の配列。おすすめの日・時間帯を太字にするため DOM で組む。
   * reset は指定なしに戻した案内 (赤茶)。それ以外 (鮮度面で非推奨の案内) は黄色。
   */
  setNotice(el, parts, reset = false) {
    if (!el) return;
    el.textContent = '';
    el.classList.toggle('shipping-delivery__notice--reset', reset);
    if (!parts || !parts.length) {
      el.hidden = true;
      return;
    }
    parts.forEach((part) => {
      if (typeof part === 'string') {
        el.appendChild(document.createTextNode(part));
      } else {
        const b = document.createElement('strong');
        b.textContent = part.strong;
        el.appendChild(b);
      }
    });
    el.hidden = false;
  }

  /**
   * 農園の休業日をはさむため、通常より早く出荷する日数。
   *
   * API は通常の出荷日 (お届け日 - 所要日数) が休業日なら前倒しの出荷日を ship_date に
   * 入れる。通常の出荷日で出せる日は ship_date が通常の出荷日なので、差が出るのは
   * 休業日で前倒しせざるを得ない日だけ。東京の午前中のように時間帯の都合で前倒しする
   * 日は early_ship_date に別に入り、ship_date は通常のままなのでここには掛からない。
   */
  holdDays(entry) {
    if (!this.options || !entry || !entry.ship_date) return 0;
    const diff = Math.round(
      (this.isoToUtcMs(entry.date) - this.isoToUtcMs(entry.ship_date)) / DeliveryDateSelector.DAY_MS,
    );
    return Math.max(0, diff - (this.options.transit_days || 0));
  }

  /** 鮮度面で非推奨の日 (休業日で前倒し) を選んだときに、理由と近くのおすすめ日を出す。日付を戻したときはその案内 */
  renderDateNotice() {
    if (this.dateResetMsg) {
      this.setNotice(this.dateNotice, [this.dateResetMsg], true);
      return;
    }
    const entry = this.selectedEntry();
    const hold = this.holdDays(entry);
    if (!entry || hold <= 0) {
      this.setNotice(this.dateNotice, null);
      return;
    }

    const dates = this.visibleDates();
    const before = dates.filter((d) => d.date < entry.date && this.holdDays(d) === 0).pop();
    const after = dates.find((d) => d.date > entry.date && this.holdDays(d) === 0);
    const picks = [before, after].filter(Boolean).map((d) => this.formatDateJP(d.date));

    const parts = [
      `${this.formatDateJP(entry.date)}のお届けは、農園の休業日をはさむため${hold}日早く準備し、` +
        `農園の冷蔵庫またはヤマト運輸で${hold}日保管してからのお届けになります。鮮度の面からおすすめしません。`,
    ];
    if (picks.length) parts.push({ strong: picks.join('・') }, 'のお届けがおすすめです。');
    this.setNotice(this.dateNotice, parts);
  }

  /**
   * 時間帯の都合で、通常より早く出荷する日数。
   *
   * 東京の午前中のように、ヤマトの最早時間帯より前に届けるには1日前倒しで出して
   * 配達店に前日のうちに着けておく必要がある。API はそういう日に early_ship_date を
   * 付け、前倒し無しで指定できる時間帯を late_slots で返す。休業日による前倒し
   * (holdDays) とは別物で、同じ日に両方が立つことはない。
   */
  slotHoldDays(entry, slot) {
    if (!entry) return this.unspecifiedDateSlotHoldDays(slot);
    if (!slot || !entry.early_ship_date || !this.options || !this.options.late_slots) return 0;
    if (this.options.late_slots.indexOf(slot) !== -1) return 0;
    const diff = Math.round(
      (this.isoToUtcMs(entry.ship_date) - this.isoToUtcMs(entry.early_ship_date)) / DeliveryDateSelector.DAY_MS,
    );
    return Math.max(0, diff);
  }

  /**
   * 日付が「指定なし」のときに選べる時間帯。
   *
   * 以前は最短日の時間帯だけにしていたので、東京のように最短日に午前中が届かない宛先では
   * 午前中が選べなかった。注文取込 (get_shipping_date) は日付なしで最早時間帯より前を
   * 指定されると所要日数を1日足し、最短の出荷日に出して翌日の午前中に届ける。
   * ここでは14日ぶんの日付で選べる時間帯をすべて並べる。
   */
  unspecifiedDateSlots() {
    if (!this.options || !this.options.dates.length) return [];
    const all = new Set();
    this.options.dates.forEach((d) => d.slots.forEach((s) => all.add(s)));
    return Array.from(all).sort();
  }

  /** 日付が「指定なし」で、最短日には届かない時間帯を選んだときに足される日数 */
  unspecifiedDateSlotHoldDays(slot) {
    if (!slot || !this.options || !this.options.dates.length) return 0;
    const reachable = this.options.late_slots || this.options.dates[0].slots;
    return reachable.indexOf(slot) === -1 ? 1 : 0;
  }

  /** 鮮度面で非推奨の時間帯 (時間帯の都合で前倒し) を選んだときに、理由とおすすめを出す。時間帯を戻したときはその案内 */
  renderTimeNotice() {
    if (this.timeResetMsg) {
      this.setNotice(this.timeNotice, [this.timeResetMsg], true);
      return;
    }
    const entry = this.selectedEntry();
    const slot = this.timeInput.value;
    const hold = this.slotHoldDays(entry, slot);
    if (hold <= 0) {
      this.setNotice(this.timeNotice, null);
      return;
    }
    const label = this.slotLabels[slot] || slot;

    if (!entry) {
      // 取込側は最短の出荷日に出して、所要日数を1日足した日に届ける
      const earliest = this.options.dates[0];
      this.setNotice(this.timeNotice, [
        `お届け日の指定なしで${label}をお選びの場合、お届けは`,
        { strong: `${this.formatDateJP(this.addDaysIso(earliest.date, hold))}${label}` },
        `になります。配達時間の都合で${hold}日早く発送し、ヤマト運輸で${hold}日お預かりしてからの` +
          'お届けになるため、鮮度の面からおすすめしません。',
        ...this.laterSlotPick(this.options.late_slots || earliest.slots),
      ]);
      return;
    }

    // 時間帯の都合の前倒しは、農園で保管するのではなく実際に1日早く発送してヤマトが預かる
    // (休業日をはさむ日は農園の冷蔵庫で保管することが多いので、そちらの文言とは分けている)
    this.setNotice(this.timeNotice, [
      `${this.formatDateJP(entry.date)}${label}のお届けは、配達時間の都合で${hold}日早く発送し、` +
        `ヤマト運輸で${hold}日お預かりしてからのお届けになります。鮮度の面からおすすめしません。`,
      ...this.laterSlotPick(entry.slots.filter((s) => this.options.late_slots.indexOf(s) !== -1)),
    ]);
  }

  /** 前倒し無しで届く時間帯のうち一番早いものを「〜以降の時間帯がおすすめです」にする */
  laterSlotPick(slots) {
    const later = slots && slots[0];
    if (!later) return [];
    return [{ strong: `${this.slotLabels[later] || later}以降` }, 'の時間帯がおすすめです。'];
  }

  selectedEntry() {
    if (!this.options || !this.dateInput.value) return null;
    return this.entryFor(this.dateInput.value);
  }

  updateZipWarning() {
    if (!this.zipWarning) return;
    this.zipWarning.hidden = !(this.checkbox.checked && !this.options);
  }

  toggleFields() {
    this.fields.hidden = !this.checkbox.checked;
    this.updateZipWarning();
    if (!this.checkbox.checked) {
      this.dateInput.value = '';
      this.timeInput.value = '';
      this.hideError();
    }
    this.dateResetMsg = '';
    this.timeResetMsg = '';
    this.render();
  }

  validate() {
    if (!this.checkbox || !this.checkbox.checked) return true;

    // 配送日時を指定したいのに選択肢を出せていない状態で通さない。
    // 以前はここで組み込みの火・土ルールに落ちて、休業日でも通していた。
    if (!this.options) {
      this.showError('お届け先の郵便番号を入力してください。お届けできる日をお調べします。');
      return false;
    }

    // ページを開いたまま日をまたぐと、選んだ日が7日の窓から外れることがある
    const dateVal = this.dateInput.value;
    const entry = this.selectedEntry();
    if (dateVal && (!entry || !this.visibleDates().some((d) => d.date === dateVal))) {
      this.showError('選択されたお届け日はご指定いただけません。選び直してください。');
      return false;
    }

    if (!this.timeAllowed(entry, this.timeInput.value)) {
      this.showError('選択された時間帯はご指定いただけません。選び直してください。');
      return false;
    }

    this.hideError();
    return true;
  }

  showError(msg) {
    this.errorDisplay.textContent = msg;
    this.errorDisplay.hidden = false;
  }

  hideError() {
    this.errorDisplay.textContent = '';
    this.errorDisplay.hidden = true;
  }

  saveAttributes() {
    const attributes = { time: '', date: '' };
    if (this.checkbox.checked) {
      attributes.time = this.timeInput.value || '';
      attributes.date = (this.dateInput.value || '').replace(/-/g, '/');
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });
  }

  /**
   * "2026-08-14" -> "8月14日（金）"
   * new Date(iso) は UTC 深夜に解釈されるので、日本より西の時間帯から見ると
   * 前日になる。曜日と日付は文字列のまま組み立てる。
   */
  formatDateJP(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = days[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
    return `${m}月${d}日（${weekday}）`;
  }

  /** "2026-10-15" -> "10/15（木）"。ボタンに収まる短い表記 */
  formatDateShort(iso) {
    const [, m, d] = iso.split('-').map(Number);
    return `${m}/${d}${this.formatDateJP(iso).slice(-3)}`;
  }
}

DeliveryDateSelector.DAY_MS = 24 * 60 * 60 * 1000;
DeliveryDateSelector.ZIP_KEY = 'kajiya:shipping-zip';

document.addEventListener('DOMContentLoaded', () => {
  window.deliveryDateSelector = new DeliveryDateSelector();
});
